import { z } from 'zod';
import { zId, zMoney } from './primitives.js';

/**
 * @deprecated Shipments are created from a consignment invoice now
 * (`zCreateShipmentFromInvoice` in `consignment-invoice.ts`); `POST /shipments` is
 * retired and no longer reads a body. Kept only so an older web bundle still
 * compiles against it during a rollout. Remove with the next contract release.
 *
 * SH-01 — a shipment covers one or more READY orders for the same retailer
 * (CR-26). Quantities default to each order line's full quantity; a caller
 * only sends `lines` to ship less than the whole order (CR-27).
 */
export const zCreateShipment = z.object({
  orderIds: z.array(zId).min(1, 'A shipment needs at least one order'),
  carrier: z.string().max(120).optional(),
  trackingReference: z.string().max(120).optional(),
  /** SH-07 — defaults to the retailer's agreed Incoterm when omitted. */
  incoterm: z.string().max(16).optional(),
  /** SH-08 */
  insuredValue: zMoney.optional(),
  dutyPaidBy: z.string().max(64).optional(),
  lines: z.array(z.object({
    orderLineId: zId,
    quantityShipped: z.number().int().positive(),
  })).optional(),
});
export type CreateShipment = z.infer<typeof zCreateShipment>;

export const zShipmentAction = z.object({
  reason: z.string().max(1000).optional(),
});
export type ShipmentAction = z.infer<typeof zShipmentAction>;

/**
 * SH-07, SH-08 — the retailer confirms a shipment arrived, and reports anything missing or
 * damaged against the packing list.
 *
 * `discrepantPieceIds` is optional and usually empty: a piece nobody disputes is received.
 * Silence on an arrived shipment means the goods are there — requiring a tick per piece would
 * leave jewellery in limbo because somebody did not finish a form.
 */
export const zConfirmReceipt = z.object({
  discrepantPieceIds: z.array(zId).max(500).optional(),
  /** Required in practice when something is disputed; the service refuses a blank one then. */
  reason: z.string().trim().max(1000).optional(),
});
export type ConfirmReceipt = z.infer<typeof zConfirmReceipt>;


/**
 * SH-02 — the travel details, edited after the fact.
 *
 * None of this is known when the shipment is created, and that is not an oversight in whoever
 * creates it: the shipping company needs the consignment invoice BEFORE it will quote a booking,
 * so the carrier, the tracking reference and the promised delivery date all arrive afterwards.
 * Until now they could only be set at creation and never corrected, which meant the common
 * case — a tracking number mistyped, or a carrier changed — had no answer at all.
 *
 * Every field is optional and independently clearable: `null` erases one, an absent key leaves
 * it alone. That distinction matters here, because "we no longer have a tracking reference"
 * and "I am only changing the carrier" are different edits and a form that cannot express
 * both will quietly overwrite with blanks.
 */
export const zUpdateShipmentDetails = z.object({
  carrier: z.string().max(120).nullish(),
  trackingReference: z.string().max(120).nullish(),
  // No courier charge here: Miraki does not charge a consignment retailer for the courier (client
  // direction, 04-Nov-2026). It returns with the purchase invoice on Sales & payables, not as a field on a shipment.
  incoterm: z.string().max(16).nullish(),
  insuredValue: zMoney.nullish(),
  dutyPaidBy: z.string().max(64).nullish(),
  /** A date, not a timestamp: a delivery is promised for a day. */
  expectedDeliveryDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a date, YYYY-MM-DD').nullish(),
}).refine((o) => Object.keys(o).length > 0, 'Nothing to change');
export type UpdateShipmentDetails = z.infer<typeof zUpdateShipmentDetails>;
